import DefaultLayout from "../../layout/DefaultLayout";
import BreadcrumbTwo from "../../components/Breadcrumbs/BreadcrumbTwo";
import {
  ShieldCheck,
  FileText,
  Database,
  RefreshCcw,
  Mail,
  AlertTriangle,
} from "lucide-react";

/**
 * 📜 Página “Términos y Condiciones” — versión extendida
 */
const AboutTerms = () => {
  return (
    <DefaultLayout>
      <BreadcrumbTwo />

      {/* 🧭 Contenedor principal */}
      <div className="flex flex-col gap-7.5">
        {/* 📘 Card principal */}
        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          {/* 🔹 Encabezado */}
          <div className="border-b border-stroke px-4 py-6 dark:border-strokedark sm:px-6 xl:px-9">
            <h3 className="text-lg font-semibold text-black dark:text-white flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Términos y Condiciones
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-3xl leading-relaxed">
              Estos términos regulan el uso del sistema <strong>Codenova POS</strong>, 
              propiedad de <strong>Codenova Studios</strong>. Al acceder o utilizar el 
              sistema, el usuario acepta cumplir con las condiciones establecidas a 
              continuación, así como con la política de privacidad correspondiente.
            </p>
          </div>

          {/* 🔸 Contenido */}
          <div className="flex flex-col gap-10 p-4 sm:p-6 xl:p-9">
            {/* 1️⃣ Uso del Sistema */}
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <FileText className="h-6 w-6" />
              </div>
              <div>
                <h4 className="mb-3 text-base font-semibold text-black dark:text-white">
                  1. Uso del Sistema
                </h4>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  Codenova POS está diseñado para facilitar la gestión comercial,
                  contable y fiscal de las empresas usuarias. Cada usuario es
                  responsable de la veracidad de los datos ingresados, así como de
                  mantener la confidencialidad de sus credenciales de acceso.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  El uso indebido del sistema, la manipulación intencionada de datos,
                  el intento de acceso no autorizado o el uso con fines distintos a los
                  permitidos constituirán una infracción a estos términos, pudiendo
                  derivar en la suspensión o cancelación de la cuenta.
                </p>
              </div>
            </div>

            {/* 2️⃣ Propiedad Intelectual */}
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <div>
                <h4 className="mb-3 text-base font-semibold text-black dark:text-white">
                  2. Propiedad Intelectual
                </h4>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  El software, código fuente, diseño, logotipos, documentación,
                  imágenes y materiales asociados son propiedad exclusiva de{" "}
                  <strong>Codenova Studios</strong>. Queda estrictamente prohibida la
                  copia, modificación, redistribución o comercialización del sistema sin
                  autorización previa y por escrito.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  Codenova POS está protegido por las leyes de derechos de autor y
                  propiedad intelectual nacionales e internacionales.
                </p>
              </div>
            </div>

            {/* 3️⃣ Protección de Datos */}
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Database className="h-6 w-6" />
              </div>
              <div>
                <h4 className="mb-3 text-base font-semibold text-black dark:text-white">
                  3. Protección y Privacidad de los Datos
                </h4>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  En <strong>Codenova POS</strong> protegemos la información de nuestros
                  usuarios aplicando medidas de seguridad técnicas, administrativas y
                  organizativas, alineadas a las buenas prácticas internacionales de
                  ciberseguridad.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  Los datos almacenados se utilizan únicamente con fines operativos,
                  tributarios y de soporte. En ningún caso se comparten o venden a
                  terceros. El usuario puede solicitar la eliminación, actualización o
                  corrección de su información conforme a la política de privacidad.
                </p>
              </div>
            </div>

            {/* 4️⃣ Actualizaciones y Mejoras */}
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <RefreshCcw className="h-6 w-6" />
              </div>
              <div>
                <h4 className="mb-3 text-base font-semibold text-black dark:text-white">
                  4. Actualizaciones, Mantenimiento y Mejoras
                </h4>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  Codenova Studios se reserva el derecho de realizar actualizaciones
                  técnicas, nuevas funcionalidades o mejoras de rendimiento que
                  contribuyan a una mejor experiencia de usuario y seguridad del sistema.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  Dichas actualizaciones podrán aplicarse sin previo aviso, garantizando
                  siempre la integridad y confidencialidad de los datos almacenados.
                </p>
              </div>
            </div>

            {/* 5️⃣ Contacto y Soporte */}
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Mail className="h-6 w-6" />
              </div>
              <div>
                <h4 className="mb-3 text-base font-semibold text-black dark:text-white">
                  5. Contacto y Soporte
                </h4>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  Nuestro equipo de soporte técnico está disponible para atender
                  consultas, incidencias o solicitudes relacionadas con el uso del
                  sistema. Puedes comunicarte con nosotros a través de los siguientes
                  canales oficiales:
                </p>
                <ul className="mt-3 flex flex-col gap-2 text-sm">
                  <li className="text-primary underline">
                    codenovasoportesv@gmail.com
                  </li>
                </ul>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  El horario de atención es de <strong>lunes a viernes, de 8:00 a.m. a
                  5:00 p.m.</strong> (hora de El Salvador), salvo feriados nacionales.
                </p>
              </div>
            </div>

            {/* ⚠️ Sección 6 — Limitación de Responsabilidad */}
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div>
                <h4 className="mb-3 text-base font-semibold text-black dark:text-white">
                  6. Limitación de Responsabilidad
                </h4>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  <strong>Codenova Studios</strong> no se hace responsable por pérdidas
                  de información ocasionadas por mal uso del sistema, errores humanos,
                  fallas de conexión o causas de fuerza mayor. Sin embargo, implementamos
                  políticas de respaldo y recuperación para minimizar riesgos.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  El usuario acepta que el uso de Codenova POS se realiza bajo su propia
                  responsabilidad, comprometiéndose a usarlo de manera ética y conforme
                  a la ley.
                </p>
              </div>
            </div>

            {/* 🧩 Nota final */}
            <div className="mt-4 border-t border-stroke pt-6 dark:border-strokedark">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed">
                Estos términos y condiciones pueden actualizarse periódicamente. El uso
                continuo del sistema implica la aceptación de su versión más reciente.{" "}
                <br />
                Última actualización: <strong>octubre 2025</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default AboutTerms;
